import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { Mail, Phone, MapPin, Send, MessageSquare, CheckCircle2, AlertCircle } from 'lucide-react';

const FORM_ACTION = 'https://readdy.ai/api/form/d86gt2vr0h3lemqac00g';

export default function Contact() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email.includes('@') || !formData.message) {
      setFormStatus('error');
      return;
    }

    const fd = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => fd.append(k, v));

    fetch(FORM_ACTION, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: fd.toString(),
    }).then(r => {
      if (r.ok) { 
        setFormStatus('success'); 
        setFormData({ name: '', email: '', message: '' }); 
      }
      else setFormStatus('error');
    }).catch(() => setFormStatus('error'));
  };

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('contact_title', 'Contactez-Nous')}
        description={t('contact_subtitle', 'Nous sommes à votre écoute. N’hésitez pas à nous contacter.')}
        breadcrumbs={[{ label: t('nav_contact', 'Contact'), href: '/contact' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Contact Info Cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-10 border-t-8 border-navy shadow-sm flex flex-col items-center text-center">
               <Phone className="w-12 h-12 text-gold mb-6" />
               <h3 className="text-2xl font-black text-navy uppercase tracking-tighter mb-4">{t('contact_info_phone')}</h3>
               <p className="text-gray-600 font-bold text-lg leading-relaxed">
                 {t('contact_info_phone_value')}
               </p>
            </div>
            <div className="bg-gray-50 p-10 border-t-8 border-navy shadow-sm flex flex-col items-center text-center">
               <Mail className="w-12 h-12 text-gold mb-6" />
               <h3 className="text-2xl font-black text-navy uppercase tracking-tighter mb-4">{t('contact_info_email')}</h3>
               <p className="text-gray-600 font-bold text-lg leading-relaxed">
                 secretariat@estad-university.com
               </p>
            </div>
          </section>

          {/* Form & Map Section */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-10">
              <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic flex items-center gap-4 border-b border-gray-100 pb-4">
                <MessageSquare className="w-8 h-8 text-gold" /> {t('contact_form_heading')}
              </h2>
              
              {formStatus === 'success' ? (
                <div className="bg-green-50 border-2 border-green-200 p-8 rounded-sm text-center space-y-4 animate-in zoom-in duration-300">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                  <h3 className="text-2xl font-black text-green-800 uppercase tracking-tighter">{t('contact_form_success')}</h3>
                  <button onClick={() => setFormStatus('idle')} className="text-green-600 font-bold underline uppercase tracking-widest text-xs">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('contact_form_name')}</label>
                      <input 
                        required
                        type="text" 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-gray-50 border-0 border-b-2 border-gray-200 p-4 outline-none focus:border-gold focus:bg-white transition-all font-bold text-navy" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('contact_form_email')}</label>
                      <input 
                        required
                        type="email" 
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-gray-50 border-0 border-b-2 border-gray-200 p-4 outline-none focus:border-gold focus:bg-white transition-all font-bold text-navy" 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('contact_form_message')}</label>
                    <textarea 
                      required
                      rows={6} 
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-gray-50 border-0 border-b-2 border-gray-200 p-4 outline-none focus:border-gold focus:bg-white transition-all font-bold text-navy resize-none"
                    ></textarea>
                  </div>

                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-500 font-bold text-xs uppercase tracking-widest animate-in slide-in-from-top-2">
                      <AlertCircle className="w-4 h-4" /> {t('contact_form_error')}
                    </div>
                  )}

                  <button type="submit" className="bg-navy text-white px-12 py-5 font-black uppercase tracking-widest hover:bg-gold hover:text-navy transition-all shadow-xl active:scale-95 flex items-center gap-4 group">
                    {t('contact_form_submit')} <Send className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </button>
                </form>
              )}
            </div>

            {/* Map Placeholder */}
            <div className="space-y-10">
               <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic flex items-center gap-4 border-b border-gray-100 pb-4">
                <MapPin className="w-8 h-8 text-gold" /> {t('contact_map_title')}
              </h2>
              <div className="aspect-square bg-gray-100 rounded-sm overflow-hidden border-4 border-gray-50 shadow-inner flex items-center justify-center relative group">
                <div className="text-center p-12 space-y-4 relative z-10">
                  <MapPin className="w-16 h-16 text-gold mx-auto group-hover:scale-110 transition-transform" />
                  <div className="text-navy font-black text-xl uppercase tracking-tighter leading-tight">
                    Palais des Congrès, Yaoundé
                  </div>
                  <p className="text-gray-500 font-medium">{t('contact_info_address_value')}</p>
                </div>
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                  <img src="/images/campus/entrance.jpg" alt="Map Location" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
